import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Button,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import { Print } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fi } from "date-fns/locale";

import { format } from "date-fns";
import { fetchJournal } from "../../../redux/slices/accounting";
import useAuth from "../../../hooks/useAuth";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const PrintableTable = styled(Table)`
  @media print {
    width: 100% !important;
    & .MuiTableCell-root {
      border: 1px solid #000 !important;
      font-size: 10px !important;
      padding: 4px !important;
    }
    & .MuiTableHead-root .MuiTableCell-root {
      background-color: #f5f5f5 !important;
      font-weight: bold !important;
    }
  }
`;

const PrintableContainer = styled("div")`
  @media print {
    & .no-print {
      display: none !important;
    }
    & .MuiBreadcrumbs-root {
      display: none !important;
    }
    & .MuiDivider-root {
      display: none !important;
    }
  }
`;

function Journal(props: any) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { activeFiscalYear, journal, status } = useAppSelector(
    (state) => state.accounting
  );

  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const handlePrint = () => {
    window.print();
  };

  const runSearch = () => {
    if (!from || !to) return;
    dispatch(
      fetchJournal({
        associationId,
        from: format(from, "yyyy-MM-dd"),
        to: format(to, "yyyy-MM-dd"),
      })
    );
  };

  useEffect(() => {
    if (activeFiscalYear) {
      setFrom(new Date(activeFiscalYear.from));
      setTo(new Date(activeFiscalYear.to));
    }
  }, [activeFiscalYear]);

  useEffect(() => {
    if (!currentUser || !activeFiscalYear) return;
    dispatch(
      fetchJournal({
        associationId,
        from: format(new Date(activeFiscalYear.from), "yyyy-MM-dd"),
        to: format(new Date(activeFiscalYear.to), "yyyy-MM-dd"),
      })
    );
  }, [dispatch, currentUser, activeFiscalYear, associationId]);

  return (
    <PrintableContainer>
      <Helmet title="Blank" />
      <Typography
        variant="h3"
        gutterBottom
        display="inline"
        className="no-print"
      >
        Päiväkirja
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2} className="no-print">
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Päiväkirja</Typography>
      </Breadcrumbs>

      <Divider my={6} className="no-print" />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card mb={6}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  Päiväkirja
                </Typography>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={fi}
                >
                  <Box
                    className="no-print"
                    display="flex"
                    gap={2}
                    alignItems="center"
                    mb={2}
                    flexWrap="wrap"
                  >
                    <DatePicker
                      label="Alkaen"
                      inputFormat="dd.MM.yyyy"
                      value={from}
                      onChange={(v) => setFrom(v)}
                      maxDate={to ?? undefined}
                      renderInput={(params) => (
                        <TextField {...params} size="small" />
                      )}
                    />
                    <DatePicker
                      label="Päättyy"
                      inputFormat="dd.MM.yyyy"
                      value={to}
                      onChange={(v) => setTo(v)}
                      minDate={from ?? undefined}
                      renderInput={(params) => (
                        <TextField {...params} size="small" />
                      )}
                    />
                    <Button variant="contained" onClick={runSearch}>
                      Hae
                    </Button>
                  </Box>
                </LocalizationProvider>
                <IconButton
                  onClick={handlePrint}
                  title="Tulosta"
                  className="no-print"
                >
                  <Print />
                </IconButton>
              </Box>

              <PrintableTable size="small">
                <TableHead>
                  <TableRow key={"header"}>
                    <TableCell>Tili</TableCell>
                    <TableCell>Kuvaus</TableCell>
                    <TableCell>Debet (€)</TableCell>
                    <TableCell>Kredit (€)</TableCell>
                    <TableCell>Pvm</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {status === "loading" ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="50vh"
                        >
                          <CircularProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (journal || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Ei kirjauksia
                      </TableCell>
                    </TableRow>
                  ) : (
                    journal.map((row: any) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          {row.account.code} - {row.account.name}
                        </TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell>
                          {new Intl.NumberFormat("fi-FI", {
                            style: "currency",
                            currency: "EUR",
                          }).format(row.debit)}
                        </TableCell>
                        <TableCell>
                          {new Intl.NumberFormat("fi-FI", {
                            style: "currency",
                            currency: "EUR",
                          }).format(row.credit)}
                        </TableCell>
                        <TableCell>
                          {format(new Date(row.date), "dd.MM.yyyy HH:mm")}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </PrintableTable>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PrintableContainer>
  );
}

export default Journal;
